import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useCondominium } from '../contexts/CondominioContext'
import { useAuth } from '../contexts/AuthContext'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useRentalOwnerAccess } from '../hooks/useRentalOwnerAccess'
import { useRentalTenantAccess } from '../hooks/useRentalTenantAccess'
import { useTenantModulos } from '../hooks/useTenantModulos'
import { MANAGEMENT_NAV } from '../navigation/navigation.config'
import { setLastVisited } from '../navigation/navState'
import { findInTree, relativePath, useNavigation } from '../navigation/useNavigation'
import { getContexto } from '../utils/contexto'
import { buildSubdomainUrl } from '../../config'
import ModuleTabs from './ModuleTabs'

export default function CondominiumLayout() {
  const { t } = useTranslation(['condominiums', 'nav', 'rentals'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { temModulo } = useTenantModulos()
  const { status: ownerAccess } = useRentalOwnerAccess()
  const { status: tenantAccess } = useRentalTenantAccess()
  const { activeCondominium, selectCondominium, clearActiveCondominium } = useCondominium()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const { user, switchEstate, exitEstate, hasPermission } = useAuth()
  const nav = useNavigation()
  // Exit (largar o estate activo, ficar em vista global) mantém-se exclusivo do ADMIN.
  const isAdmin = hasPermission('ESTATE_CONTEXT_EXIT')
  const canSwitchEstates = (user?.estateIds?.length ?? 0) > 1

  useEffect(() => {
    if (!id) return

    let cancelled = false
    setIsLoading(true)
    setError(false)

    // A sessão tem de estar neste condomínio: muitos endpoints só olham para o estate do token. Um link
    // directo pode chegar aqui com outro condomínio activo, ou com nenhum (ADMIN depois de "Sair"). Troca-se
    // ANTES de o carregar — com outro condomínio na sessão, o GET deste devolve 404.
    const precisaTrocar = (canSwitchEstates || isAdmin) && user?.estateId !== id
    ;(precisaTrocar ? switchEstate(id) : Promise.resolve())
      .then(() => selectCondominium(id))
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [id, selectCondominium, canSwitchEstates])

  // Cada tipo vive no seu subdomínio (cond./prop.). Um link ou um selector que abra uma propriedade
  // em cond. (ou o contrário) leva ao certo — a sessão é do domínio raiz, segue com o pedido.
  // Em localhost (NEUTRO) não há subdomínios.
  const tipoCarregado = activeCondominium?.id === id ? activeCondominium?.tipo : undefined
  useEffect(() => {
    const contexto = getContexto()
    if (!tipoCarregado || contexto === 'NEUTRO') return
    const certo = tipoCarregado === 'PROPERTIES' ? 'PROPRIEDADE' : 'CONDOMINIO'
    if (contexto === certo) return
    window.location.href = buildSubdomainUrl(certo === 'PROPRIEDADE' ? 'prop' : 'cond',
      `${location.pathname}${location.search}${location.hash}`)
  }, [tipoCarregado, location.pathname, location.search, location.hash])

  const backToList = async () => {
    clearActiveCondominium()
    if (isAdmin) await exitEstate()  // ← remove o estate activo do token JWT; exit é exclusivo do ADMIN
    navigate('/properties')
  }

  // Página do URL no menu deste perfil (separadores) e na árvore completa (guards).
  const active = nav.findActive(location.pathname)
  const rel = id ? relativePath(location.pathname, id) : null
  const canonical = rel === null ? null : findInTree(MANAGEMENT_NAV, rel)

  // Lembra o último separador de cada módulo (o menu volta a ele).
  const activeModuleId = active?.module.id
  const activeTabId = active?.tab.id
  const activeLeafPath = active && !active.leaf.hiddenInNav ? active.leaf.path : null
  useEffect(() => {
    if (!id || !activeModuleId || !activeTabId) return
    setLastVisited(id, activeModuleId, activeTabId)
    if (activeLeafPath !== null) setLastVisited(id, `${activeModuleId}/${activeTabId}`, activeLeafPath)
  }, [id, activeModuleId, activeTabId, activeLeafPath])

  useDocumentTitle(active ? t(active.leaf.label) : null, activeCondominium?.nome)

  // Rota de um módulo que o plano do tenant não inclui — o item do menu já
  // desaparece, isto trava o acesso por link directo.
  const moduloForaDoPlano = !!canonical?.module.modulo && !temModulo(canonical.module.modulo)
  // Arrendamento é self-service (permissões *_PROPRIO, resolvidas pela
  // entidade do próprio utilizador). Sem entidade associada não há nada
  // "próprio" — trava o acesso directo por URL, tal como o item do menu.
  // Excepção: numa propriedade, a gestora gere os contratos de todos sem ser condómina.
  const gestoraDeArrendamentos = nav.rentalManagement.active
  const exigeEntidade = !!canonical?.module.memberOnly && !user?.temEntidade
    && !(canonical?.module.id === 'rentals' && gestoraDeArrendamentos)
  // Contratos, Receitas e Recebimentos são do lado do proprietário (ou da gestora, numa propriedade); "Meu
  // arrendamento" e "Minha conta" do lado do inquilino — nem por link directo.
  const paginaDeProprietario = canonical?.leaf.rental === 'owner' && !gestoraDeArrendamentos
  const paginaDeInquilino = canonical?.leaf.rental === 'tenant'
  const exigeProprietario = paginaDeProprietario && ownerAccess === 'hidden'
  const exigeInquilino = paginaDeInquilino && tenantAccess === 'hidden'
  // Finanças do estate: um condómino só as vê como membro da comissão, no modo "Ver como comissão" — ou, numa
  // propriedade, se for proprietário.
  const paginaDaComissao = !!canonical?.leaf.committeeFinance && nav.profile === 'condomino'
  const exigeComissao = paginaDaComissao && !nav.financeViewLoading && !nav.financeView
  // Prestação de contas: a gestora, ou quem é (ou foi) proprietário — o seu extracto.
  const paginaDoExtracto = !!canonical?.leaf.ownerStatement && nav.ePropriedade
  const exigeExtracto = paginaDoExtracto && nav.propertyAccessStatus !== 'loading'
    && !nav.propertyAccess.gestora && !nav.propertyAccess.temExtracto
  // Enquanto o servidor não responde não se mostra a página (um não-proprietário veria um clarão dela).
  const aVerificar = (paginaDeProprietario && ownerAccess === 'loading') || (paginaDeInquilino && tenantAccess === 'loading')
    || (paginaDaComissao && nav.financeViewLoading) || (paginaDoExtracto && nav.propertyAccessStatus === 'loading')

  // Comissão e assembleias não existem numa propriedade — o menu já as esconde, isto trava o link directo.
  const apenasCondominio = nav.ePropriedade && !!(canonical?.module.condominiumOnly || canonical?.leaf.condominiumOnly)
  // E a prestação de contas ao proprietário só existe numa propriedade.
  const apenasPropriedade = !nav.ePropriedade && !!canonical?.leaf.propertyOnly

  const rotaBloqueada = apenasCondominio || apenasPropriedade || moduloForaDoPlano || exigeEntidade || exigeProprietario
    || exigeInquilino || exigeComissao || exigeExtracto
  const mensagemBloqueio = apenasCondominio
    ? t('blocked.condominiumOnly')
    : apenasPropriedade
    ? t('blocked.propertyOnly')
    : exigeExtracto
    ? t('blocked.statementOnly')
    : moduloForaDoPlano
      ? t('blocked.moduleNotInPlan')
      : exigeEntidade
        ? t('blocked.membersOnly')
        : exigeInquilino
          ? t('blocked.tenantOnly')
          : exigeProprietario
            ? t('blocked.ownerOnly')
            : nav.ePropriedade ? t('blocked.propertyFinanceOnly')
            : nav.isCommitteeMember ? t('blocked.committeeViewOff') : t('blocked.committeeOnly')

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !activeCondominium) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">
          {error ? t('context.loadError') : t('context.notFound')}
        </Alert>
        <Button sx={{ mt: 2 }} onClick={backToList} startIcon={<ArrowBackRoundedIcon />}>
          {t('context.backToList')}
        </Button>
      </Container>
    )
  }

  return (
    <Box>
      {active && !rotaBloqueada && <ModuleTabs active={active} />}

      {/* Conteúdo específico do condomínio activo (rotas filhas) */}
      {rotaBloqueada ? (
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Alert severity="info">
            {mensagemBloqueio}
          </Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate(`/properties/${id}`)} startIcon={<ArrowBackRoundedIcon />}>
            {t('context.backToDashboard')}
          </Button>
        </Container>
      ) : aVerificar ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        // Suspense local: enquanto o chunk da página filha (lazy) carrega, a
        // sidebar e os separadores ficam de pé — só a área de conteúdo gira.
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          }
        >
          <Outlet />
        </Suspense>
      )}
    </Box>
  )
}
